import VatSection from "./vat/VatWizard.jsx";
import PayrollSection from "./payroll/PayrollWizard.jsx";
import FinSection from "./fin/FinWizard.jsx";
import { useEffect, useState } from "react";
import { api, download, cabDateTime } from "../api.js";
import { SectionHead, PartialErrors, Empty } from "./common.jsx";
import RegdocView from "./RegdocView.jsx";
import VatUr from "./vat/VatUr.jsx";
import PayrollUr from "./payroll/PayrollUr.jsx";

// Стан документа — як у дашборді etax (kvt: 0 чернетка, 1 доставлено, 2 оброблено)
export const STATE = {
  accepted: ["Прийнято", "ok"], delivered: ["Доставлено", "warn"], signed: ["Підписано", "warn"],
  draft: ["Чернетка", "idle"], error: ["Помилка", "bad"],
};
const periodText = (r) => [r.period_name, r.period_year].filter(Boolean).join(" ");

function StateBadge({ state }) {
  const [t, tone] = STATE[state] || [state, "idle"];
  return <span className={`badge badge-${tone}`}>{t}</span>;
}

// Кнопка завантаження: помилку (наприклад, «потрібен вхід ключем») показує поруч
export function DownloadButton({ path, children, block = false, small = true }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    setBusy(true); setError(null);
    try { await download(path); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <span className={`download${block ? " download-block" : ""}`}>
      <button type="button" className={`btn btn-secondary${small && !block ? " btn-sm" : ""}${block ? " btn-block" : ""}`} onClick={go} disabled={busy}>
        {busy ? "Завантаження…" : children}
      </button>
      {error && <span className="error" role="alert">{error}</span>}
    </span>
  );
}

function Receipts({ list, base }) {
  return (
    <ol className="receipts">
      {list.map((k) => (
        <li key={k.id}>
          <div className="row">
            <div>
              <b>Квитанція № {k.num_kvt}{k.final_kvt ? " (остаточна)" : ""}</b>
              <span className="muted"> · {k.cdoc} · {cabDateTime(k.oper_date)}</span>
            </div>
            {k.has_pdf && <DownloadButton path={`${base}/receipts/${k.id}/pdf`}>PDF квитанції</DownloadButton>}
          </div>
          <p className="receipt-text">{k.info}</p>
        </li>
      ))}
    </ol>
  );
}

function ReportDetails({ companyId, id }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api(`/api/companies/${companyId}/reports/${id}`).then(setD); }, [companyId, id]);
  if (!d) return <p className="muted">Завантаження…</p>;
  const base = `/api/companies/${companyId}`;
  const r = d.report;
  return (
    <div className="lines">
      <div className="row">
        <div>
          <b>{r.cdoc} · {r.doc_name}</b>
          <p className="muted">{periodText(r)}{r.oper_date ? ` · подано ${cabDateTime(r.oper_date)}` : ""}</p>
        </div>
        <div className="actions">
          <DownloadButton path={`${base}/reports/${id}/pdf`}>PDF документа</DownloadButton>
          <DownloadButton path={`${base}/reports/${id}/xml`}>XML</DownloadButton>
        </div>
      </div>
      {d.appendices.length > 0 && (
        <>
          <h3 className="h4">Додатки</h3>
          <ul className="plain appendices">
            {d.appendices.map((a) => (
              <li key={a.id}>
                <div>{a.cdoc} · {a.doc_name} <StateBadge state={a.state} /></div>
                {a.receipts.length > 0 && <Receipts list={a.receipts} base={base} />}
              </li>
            ))}
          </ul>
        </>
      )}
      <h3 className="h4">Квитанції</h3>
      {!d.receipts.length
        ? <Empty>{(r.kvt ?? 0) > 0 ? "Квитанції ще не завантажено — оновіть розділ." : "Документ не подано — квитанцій немає."}</Empty>
        : <Receipts list={d.receipts} base={base} />}
    </div>
  );
}

// Дві вкладки, як у кабінеті ДПС: «Усі подані» (Перегляд звітності — будь-яким шляхом, зокрема поштою з інших
// програм) і «Створені в кабінеті» (документи кабінету з квитанціями, синхронізуються)
export default function Reports(props) {
  // Майстри: #/c/<id>/reports/vat[/чернетка], #/c/<id>/reports/payroll[/чернетка]
  const wiz = ["vat", "payroll", "fin"].find((k) => String(props.sub || "").split("/")[0] === k) || null;
  const [tab, setTab] = useState(wiz || (props.sub ? "cabinet" : "all"));
  const pick = (k) => { setTab(k); if (["vat", "payroll", "fin"].includes(k)) location.hash = `#/c/${props.companyId}/reports/${k}`; else if (wiz) location.hash = `#/c/${props.companyId}/reports`; };
  const draftId = wiz ? String(props.sub).split("/")[1] || null : null;
  // Уточнюючий розрахунок: #/c/<id>/reports/ur/<чернетка>
  const ur = String(props.sub || "").match(/^ur\/([0-9a-f-]{36})$/)?.[1];
  if (ur) return <VatUr key={ur} companyId={props.companyId} company={props.company} draftId={ur} version={props.version} />;
  const pur = String(props.sub || "").match(/^pur\/([0-9a-f-]{36})$/)?.[1];
  if (pur) return <PayrollUr key={pur} companyId={props.companyId} company={props.company} draftId={pur} version={props.version} />;
  return (
    <>
      <SectionHead title="Звітність і квитанції" res={props.res} />
      <PartialErrors res={props.res} />
      <div className="tabs reports-tabs" role="group" aria-label="Звітність">
        {[["vat", "Декларація з ПДВ"], ["payroll", "ПДФО / ВЗ / ЄСВ"], ["fin", "Фінзвітність"], ["all", "Усі подані"], ["cabinet", "Створені в кабінеті"]]
          .filter(([k]) => !["vat", "payroll", "fin"].includes(k) || !props.capabilities || props.capabilities.reports?.[k] !== false).map(([k, l]) => (
          <button type="button" key={k} className={tab === k ? "tab active" : "tab"} aria-pressed={tab === k} onClick={() => pick(k)}>{l}</button>
        ))}
      </div>
      {tab === "vat" ? <VatSection companyId={props.companyId} company={props.company} draftId={wiz === "vat" ? draftId : null} version={props.version} />
        : tab === "payroll" ? <PayrollSection companyId={props.companyId} company={props.company} draftId={wiz === "payroll" ? draftId : null} version={props.version} />
        : tab === "fin" ? <FinSection companyId={props.companyId} company={props.company} draftId={wiz === "fin" ? draftId : null} version={props.version} />
        : tab === "all" ? <RegdocView companyId={props.companyId} company={props.company} /> : <CabinetReports {...props} sub={wiz ? null : props.sub} />}
    </>
  );
}

function CabinetReports({ companyId, sub, res, version, onSync }) {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  // Звіт із пошуку в шапці (#/c/<id>/reports/<id звіту>) — розгорнутий і в полі зору
  useEffect(() => {
    if (!sub || !data) return;
    const r = data.reports.find((x) => String(x.id) === sub);
    if (!r) return;
    setOpen(r.id);
    setTimeout(() => document.querySelector(`[data-report="${r.id}"]`)?.scrollIntoView({ block: "center" }), 0);
  }, [sub, data]);
  useEffect(() => {
    api(`/api/companies/${companyId}/reports${year ? `?year=${year}` : ""}`).then(setData);
  }, [companyId, year, version]);
  const toggle = (id) => setOpen(open === id ? null : id);
  return (
    <>
      <div className="card">
        {data?.years?.length > 0 && (
          <div className="erpn-filters">
            <select className="erpn-month" aria-label="Рік" value={year ?? ""} onChange={(e) => { setYear(e.target.value ? Number(e.target.value) : null); setOpen(null); }}>
              <option value="">Усі роки</option>
              {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="muted small">Документи, створені й подані в самому кабінеті ДПС, з квитанціями</span>
          </div>
        )}
        {!data ? <p className="muted">Завантаження…</p> : !data.reports.length ? <Empty>Звітності ще немає. Натисніть «Оновити Звітність» вгорі.</Empty> : (
          <div className="table-wrap">
            <table className="reports-table">
              <thead><tr><th>Період</th><th>Форма</th><th>Стан</th><th>Подано</th><th>Остання квитанція</th></tr></thead>
              <tbody>
                {data.reports.map((r) => (
                  <ReportRow key={r.id} r={r} open={open === r.id} onToggle={() => toggle(r.id)} companyId={companyId} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function ReportRow({ r, open, onToggle, companyId }) {
  return (
    <>
      {/* Розгортання — кнопкою в першій клітинці (aria-expanded на рядку звичайної таблиці не дозволено); клік по рядку — як і раніше */}
      <tr data-report={r.id} className={open ? "selected" : ""} onClick={onToggle}>
        <td data-label="Період"><button type="button" className="link row-toggle" aria-expanded={open}>{periodText(r)}</button></td>
        <td data-label="Форма" className="wrap-cell"><b>{r.cdoc}</b> {r.doc_name}{r.appendices ? <span className="muted"> · додатків {r.appendices}</span> : null}</td>
        <td data-label="Стан"><StateBadge state={r.state} /></td>
        <td data-label="Подано">{cabDateTime(r.oper_date)}</td>
        <td data-label="Остання квитанція" className="wrap-cell muted">{(r.last_receipt || "").split(/\r?\n/)[0]}</td>
      </tr>
      {open && (
        <tr className="details-row"><td colSpan={5}><ReportDetails companyId={companyId} id={r.id} /></td></tr>
      )}
    </>
  );
}
